import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter } from 'react-icons/lu';
import { PiScanFill } from "react-icons/pi";
import { GrAttachment, GrInbox } from "react-icons/gr";
import { FaTruck } from 'react-icons/fa';
import { IoSettingsOutline } from "react-icons/io5";
import Header from '../../../header/header';
import Navvbar from '../../../header/Navvbar';
import axios from "axios";
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

const CasesArrive = () => {
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [accounts] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('forever');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lab vs Admin Logic
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [labAccounts, setLabAccounts] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const rowsPerPage = 5;

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Load user type & labId from localStorage
  useEffect(() => {
    let storedLabId = localStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType');

    if (!storedLabId) {
      storedLabId = sessionStorage.getItem('userLabId');
    }
    if (!storedUserType) {
      storedUserType = sessionStorage.getItem('userType');
    }

    const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (!storedLabId && userData.labId) storedLabId = userData.labId;
        if (!storedUserType && userData.userType) storedUserType = userData.userType;
      } catch (e) {
        // ignore
      }
    }

    if (storedLabId) setLabId(storedLabId);
    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }

    setAuthLoaded(true);
  }, []);

  // Fetch lab accounts if lab user
  useEffect(() => {
    if (!authLoaded || userType !== 'lab' || !labId) return;

    const fetchLabAccounts = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/account/lab-accounts/${labId}`
        );
        if (response.data.success) {
          setLabAccounts(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching lab accounts:', err);
        setLabAccounts([]);
      }
    };

    fetchLabAccounts();
  }, [authLoaded, userType, labId]);

  // Date ranges
  const getDateRanges = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      today: formatDate(today),
      tomorrow: formatDate(tomorrow),
      weekStart: formatDate(weekStart),
      weekEnd: formatDate(weekEnd),
      monthStart: formatDate(monthStart),
      monthEnd: formatDate(monthEnd),
    };
  };

  // ✅ FIXED: Fetch from /api/cases/lab-cases (endpoint that exists)
  useEffect(() => {
    if (!authLoaded) return;

    const fetchCases = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Filter from URL
        const params = new URLSearchParams(location.search);
        let filterType = params.get('filter') || 'forever';
        setSelectedFilter(filterType);

        // ✅ Build API call to /api/cases/lab-cases
        const apiParams = {};
        if (labId) apiParams.userLabId = labId;
        if (userType === 'lab' && labId) {
          // Some backends want accountId filter; pass it if you want.
          // Here we keep it broad and filter client-side.
        }

        const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
          params: apiParams,
        });

        // ✅ Handle both { success, data } and plain array response shapes
        let rawCases = [];
        if (Array.isArray(response.data)) {
          rawCases = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          rawCases = response.data.data;
        } else {
          rawCases = [];
        }

        // ✅ Keep only cases that have arrive_date
        let arrivedCases = rawCases.filter(c => c && c.arrive_date);

        // ✅ If lab user, filter by lab's account IDs
        if (userType === 'lab' && labId) {
          if (labAccounts.length > 0) {
            const labAccountIds = labAccounts
              .map(acc => acc.accountId)
              .filter(Boolean);
            arrivedCases = arrivedCases.filter(c =>
              c.accountId && labAccountIds.includes(c.accountId)
            );
          } else {
            arrivedCases = [];
          }
        }

        // ✅ Client-side date filtering (same as before)
        const dateRanges = getDateRanges();
        switch (filterType) {
          case 'today':
            arrivedCases = arrivedCases.filter(c => {
              if (!c.arrive_date) return false;
              return formatDate(new Date(c.arrive_date)) === dateRanges.today;
            });
            break;
          case 'thisweek': {
            const weekStart = new Date(dateRanges.weekStart);
            const weekEnd = new Date(dateRanges.weekEnd);
            weekEnd.setHours(23, 59, 59, 999);
            arrivedCases = arrivedCases.filter(c => {
              if (!c.arrive_date) return false;
              const d = new Date(c.arrive_date);
              return d >= weekStart && d <= weekEnd;
            });
            break;
          }
          case 'thismonth': {
            const monthStart = new Date(dateRanges.monthStart);
            const monthEnd = new Date(dateRanges.monthEnd);
            monthEnd.setHours(23, 59, 59, 999);
            arrivedCases = arrivedCases.filter(c => {
              if (!c.arrive_date) return false;
              const d = new Date(c.arrive_date);
              return d >= monthStart && d <= monthEnd;
            });
            break;
          }
          case 'forever':
          default:
            // no filter
            break;
        }

        setFilteredCases(arrivedCases);
        setAllCases(arrivedCases);
      } catch (err) {
        console.error("Error fetching cases:", err);
        setError(
          err.response?.data?.message ||
          "Failed to load cases. Please try again."
        );
        setFilteredCases([]);
        setAllCases([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCases();
  }, [location.search, authLoaded, userType, labId, labAccounts]);

  const handleSelectCase = (caseId) => {
    setSelectedCases((prev) =>
      prev.includes(caseId) ? prev.filter(id => id !== caseId) : [...prev, caseId]
    );
  };

  const handleSelectAll = () => {
    setSelectedCases(
      selectedCases.length === filteredCases.length && filteredCases.length > 0
        ? []
        : filteredCases.map(c => c._id)
    );
  };

  const toggleDropdown = (caseId) => {
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  const handleShowModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);

  const handleAccountClick = (caseItem) => {
    if (caseItem.accountId) {
      navigate(`/tabsWorking/${caseItem.accountId}`, {
        state: {
          accountId: caseItem.accountId,
          accountName: caseItem.account_name,
          accountData: caseItem,
        },
      });
    } else {
      navigate('/TabsWorking', {
        state: {
          accountName: caseItem.account_name,
          accountData: caseItem,
        },
      });
    }
  };

  const applyManualFilter = async (filterType) => {
    setIsLoading(true);
    setSelectedFilter(filterType);
    navigate(`/casesArrive?filter=${filterType}`);
  };

  const paginatedCases = filteredCases.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const getFilterDescription = () => {
    switch (selectedFilter) {
      case 'today': return 'Showing arrived cases for today';
      case 'thisweek': return 'Showing arrived cases for this week';
      case 'thismonth': return 'Showing arrived cases for this month';
      case 'forever': return 'Showing all arrived cases';
      default: return 'Showing all arrived cases';
    }
  };

  const getUserInfo = () => {
    if (userType === 'lab') return `Lab User (ID: ${labId}) - Showing only lab cases`;
    if (userType === 'admin') return 'Admin User - Showing all cases';
    return 'Loading user info...';
  };

  if (!authLoaded) {
    return (
      <>
        <Header />
        <Navvbar />
        <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px", textAlign: 'center' }}>
          <div>Loading user information...</div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <Navvbar />
        <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px", textAlign: 'center' }}>
          <div>Loading {userType === 'lab' ? 'lab ' : ''}cases...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Navvbar />
      <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px" }}>

        {error && (
          <div style={{
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#ffe6e6',
            borderRadius: '5px',
            border: '1px solid #ffcccc',
            color: '#d63031'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="title" style={{ margin: 0, backgroundColor: "white" }}>
            Arrived Cases
          </h2>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '15px' }}>
            <Button
              style={{
                backgroundColor: selectedCases.length > 0 ? '#004aad' : '#ccc',
                borderColor: "#ccc",
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
              disabled={selectedCases.length === 0}
              onClick={() => setShowModal(true)}
            >
              <FaTruck /> Mark as Delivered ({selectedCases.length})
            </Button>
            <Button style={{
              backgroundColor: "#004aad",
              borderRadius: '5px',
              borderColor: "#004aad",
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <Link to="/Scan" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <PiScanFill /> Scan
              </Link>
            </Button>
            <button style={{
              padding: '5px',
              backgroundColor: '#004aad',
              color: 'white',
              width: '90px',
              height: '35px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'
            }} onClick={handleShowModal}>
              <LuFilter /> Filters
            </button>
          </div>
        </div>

        <div style={{ width: "100%", margin: "0 auto", padding: "20px", border: "3px solid #f4f4f4", borderRadius: "5px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCases.length === filteredCases.length && filteredCases.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                {["Inbox", "Attachment", "Case/Pan", "Invoice", "Account", "Provider", "Patient/Appt", "Lab Ship", "Account Ship", "Arrive Date"]
                  .map((header) => (
                    <th key={header} style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd", fontWeight: "bold" }}>
                      {header === "Inbox" ? <GrInbox style={{ fontSize: "20px" }} /> :
                        header === "Attachment" ? <GrAttachment style={{ fontSize: "20px" }} /> : header}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {paginatedCases.length > 0 ? paginatedCases.map((caseItem) => (
                <tr key={caseItem._id} style={{ borderBottom: "1px solid #ddd", cursor: "pointer" }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCases.includes(caseItem._id)}
                      onChange={() => handleSelectCase(caseItem._id)}
                    />
                  </td>
                  <td>
                    <GrInbox style={{ fontSize: "20px" }} />
                  </td>
                  <td>
                    <GrAttachment style={{ fontSize: "20px" }} />
                  </td>
                  <td style={{ padding: "10px", textAlign: "left", position: "relative" }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Link to={`/case/${caseItem.caseNumber}`} style={{ textDecoration: "none", color: "#004aad", fontSize: "17px" }}>
                            #{caseItem.caseNumber}
                          </Link>
                          <button
                            style={{
                              fontSize: "18px",
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(caseItem._id);
                            }}
                          >
                            <IoSettingsOutline style={{ color: "#004aad", fontSize: "22px" }} />
                          </button>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "5px" }}>
                          <p style={{ margin: 0 }}>Pan Color:</p>
                          <span
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: caseItem.color?.hex || caseItem.color || "#ccc",
                              display: "inline-block",
                              borderRadius: "3px"
                            }}
                          ></span>
                          <span>{caseItem.color?.name || (typeof caseItem.color === 'string' ? caseItem.color : '')}</span>
                        </div>
                        {caseItem.procedures && caseItem.procedures.length > 0 ? (
                          <div style={{ fontSize: "14px" }}>
                            <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                              {caseItem.procedures.map((proc, index) => (
                                <li key={index} style={{ marginBottom: '5px' }}>
                                  {proc.procedure}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div style={{ fontSize: "14px", color: "#666" }}>No procedures selected</div>
                        )}
                      </div>
                    </div>
                    {dropdownVisible === caseItem._id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: "0",
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          padding: "10px",
                          borderRadius: "5px",
                          zIndex: 1000,
                          width: "150px",
                          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <ul style={{ listStyle: "none", padding: "0" }}>
                          <li style={{ padding: "8px 0", cursor: "pointer" }}>
                            <BsFileEarmarkCode /> Close/Invoice
                          </li>
                          <li style={{ padding: "8px 0", cursor: "pointer" }}>
                            <BsPencilFill /> Add Notes
                          </li>
                          <li style={{ padding: "8px 0", cursor: "pointer" }}>
                            <AiOutlineFileAdd /> Upload Files
                          </li>
                        </ul>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.invoiceNumber || ""}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    <span
                      onClick={() => handleAccountClick(caseItem)}
                      style={{
                        textDecoration: "none",
                        color: "#004aad",
                        fontSize: "16px",
                        cursor: "pointer",
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {caseItem.account_name || caseItem.account_holder || "N/A"}
                    </span>
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.provider_name || "N/A"}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.last_name || "N/A"}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.start_date ? new Date(caseItem.start_date).toLocaleDateString() : "N/A"}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : "N/A"}
                  </td>
                  <td style={{ padding: "10px", textAlign: "left" }}>
                    {caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : "N/A"}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>
                    {userType === 'lab'
                      ? `No arrived cases found for lab ${labId} with the selected filter.`
                      : 'No arrived cases found for the selected filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <br />

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '100%' }}>
          <Button
            style={{ backgroundColor: '#f0f0f0', borderColor: '#ced4da', width: '20%', color: 'black', borderRadius: '0' }}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </Button>
          <Button
            style={{ backgroundColor: '#f0f0f0', borderColor: '#ced4da', width: '20%', color: 'black', borderRadius: '0' }}
            onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button style={{ backgroundColor: '#004aad', borderColor: "#004aad", width: '20%', borderRadius: '0' }}>
            Page {currentPage} of {Math.ceil(filteredCases.length / rowsPerPage) || 1}
          </Button>
          <Button
            style={{ backgroundColor: '#f0f0f0', borderColor: '#ced4da', width: '20%', color: 'black', borderRadius: '0' }}
            onClick={() => setCurrentPage(currentPage < Math.ceil(filteredCases.length / rowsPerPage) ? currentPage + 1 : currentPage)}
            disabled={currentPage >= Math.ceil(filteredCases.length / rowsPerPage)}
          >
            Next
          </Button>
          <Button
            style={{ backgroundColor: '#f0f0f0', borderColor: '#ced4da', width: '20%', color: 'black', borderRadius: '0' }}
            onClick={() => setCurrentPage(Math.ceil(filteredCases.length / rowsPerPage))}
            disabled={currentPage >= Math.ceil(filteredCases.length / rowsPerPage)}
          >
            Last
          </Button>
        </div>
      </div>

      {show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
          onClick={handleCloseModal}
        />
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: "#004aad", color: "white" }}>
          <Modal.Title>Confirm Delivery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>List of cases to be delivered</p>
          {selectedCases.length > 0 ? (
            <ul>
              {selectedCases.map((caseId) => {
                const selectedCase = paginatedCases.find((caseItem) => caseItem._id === caseId);
                return (
                  <li key={caseId}>
                    Case # {selectedCase ? selectedCase.caseNumber : "N/A"}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>No cases selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button style={{ backgroundColor: "#004aad", borderColor: '#004aad' }} onClick={() => setShowModal(false)}>
            Deliver
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CasesArrive;